import {
  useDatasetSyncConfig,
  useDatasetSyncActions,
  useDatasetSyncConfigOptions
} from "./useDatasetSyncConfig";
import {
  Button,
  CircularLoader,
  Field,
  Icon,
  Modal,
  ModalProps,
  Select,
  Stack,
  Title,
  useTheme
} from "@introist/react-foundation/v2";
import { useCallback, useState } from "react";
import { DataSource, useDataSources } from "../../datasources/hooks/useDataSources";
import { DataSourceIcon } from "../../datasources/components/DataSourceIcon";
import { FormFooter } from "../../datasources/components/FormFooter";
import { api } from "../../../services/rpc/RpcProvider";
import { useDatasetSyncLogs } from "./useDatasetSyncLogs";
import { useNavigate } from "react-router";

const SyncStatusIcon = ({ active, lastStatus }: { lastStatus: string | null; active: boolean }) => {
  const { theme } = useTheme();

  if (!active) return <Icon name="pause" color={theme.palette.foreground.dimmed} />;

  if (lastStatus === "success") {
    return <Icon name="checkCircle" color={theme.palette.success.default} />;
  }
  if (lastStatus === "failed") {
    return <Icon name="warning" color={theme.palette.danger.default} />;
  }

  return null;
};

export const DatasetSyncButton = ({ datasetId }: { datasetId: string }) => {
  const navigate = useNavigate();
  const { config } = useDatasetSyncConfig(datasetId);

  const { data: ds } = api.employees.dataSources.find.useQuery(
    { dataSourceId: config?.dataSourceId ?? "" },
    { enabled: !!config }
  );

  const { logs } = useDatasetSyncLogs(datasetId);

  const [createOpen, setCreateOpen] = useState(false);

  if (config === undefined) return null;

  if (config === null) {
    return (
      <>
        <Button
          variant="outlined"
          size="small"
          startIcon="arrowsLeftRight"
          onClick={() => setCreateOpen(true)}
        >
          Set up sync
        </Button>
        <CreateSyncModal
          datasetId={datasetId}
          open={createOpen}
          onClose={() => setCreateOpen(false)}
        />
      </>
    );
  }

  if (!ds) return null;

  return (
    <Button variant="outlined" onClick={() => navigate("sync", { relative: "route" })}>
      <Stack>
        <DataSourceIcon dataSource={config!.sourceSystem} size="small" />
        <Title variant="bold">Sync from {ds?.name}</Title>
        <SyncStatusIcon
          active={config.syncEnabled}
          lastStatus={logs && logs.length > 0 ? logs[0].status : null}
        />
      </Stack>
    </Button>
  );
};

const CreateSyncModal = ({
  datasetId,
  open,
  onClose,
  ...rest
}: ModalProps & { onClose: () => unknown; datasetId: string }) => {
  const navigate = useNavigate();
  const { dataSources } = useDataSources();

  const { data: workspace } = api.workspaces.find.useQuery({});

  const [source, setSource] = useState<DataSource | null>(null);
  const [objectType, setObjectType] = useState<string | null>(null);

  const { options, loading: optionsLoading } = useDatasetSyncConfigOptions(source?.id ?? null);

  const { create } = useDatasetSyncActions();

  const close = useCallback(() => {
    setSource(null);
    setObjectType(null);
    onClose();
  }, [onClose]);

  const onCreate = useCallback(async () => {
    await create({
      datasetId,
      dataSourceId: source!.id,
      objectType: objectType!,
      syncDays: [1, 2, 3, 4, 5],
      syncHours: [6, 10, 14],
      timezone: workspace?.defaultTimezone ?? "UTC"
    }).then(() => {
      onClose();
      navigate("sync", { relative: "route" });
    });
  }, [source, objectType, onClose, datasetId, create, navigate, workspace]);

  return (
    <Modal open={open} onClose={onClose} title="Set up sync" {...rest}>
      <Stack vertical style={{ width: "300px" }}>
        <Field title="Data source">
          <Select
            size="small"
            style={{ width: "100%" }}
            options={(dataSources ?? []).map(s => ({
              key: s.id,
              title: s.name,
              startAdornment: <DataSourceIcon size="small" dataSource={s.sourceType} />
            }))}
            startAdornment={
              source ? <DataSourceIcon size="small" dataSource={source.sourceType} /> : null
            }
            onSelect={opt => setSource(dataSources?.find(s => s.id === opt.key) ?? null)}
            value={source?.id}
          />
        </Field>
        {options && !optionsLoading && (
          <Field title="Data type">
            <Select
              size="small"
              style={{ width: "100%" }}
              options={options.map(o => ({ key: o.objectType }))}
              placeholder="Select data type"
              onSelect={opt => setObjectType(opt.key)}
              value={objectType ?? undefined}
            />
          </Field>
        )}
        {optionsLoading && (
          <div style={{ height: "60px" }}>
            <CircularLoader size="small" fillParent />
          </div>
        )}
        {source && !options && !optionsLoading && <Title>This source has no sync options</Title>}
        <FormFooter
          onCancel={close}
          onSubmit={onCreate}
          submitDisabled={!objectType}
          submit="Create"
        />
      </Stack>
    </Modal>
  );
};
